import { useMemo, useState, useEffect } from 'react';
import {
  VizCard,
  NowYouTry,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// EnergyLevels — the principal-energy-level reference table: for each level n,
// how many sublevels (= n), which sublevels (s,p,d,f,g…) and how many orbitals
// each has (2ℓ+1 = 1,3,5,7,9), and the maximum electrons (2n²). Makes the pattern
// visible: level n → n sublevels → 2n² electrons.
// config: { upTo?: number (default 4), highlightN?: number, title? }

const SUBLEVEL_LETTERS = ['s', 'p', 'd', 'f', 'g', 'h', 'i'];
const SUBLEVEL_COLORS: Record<string, string> = {
  s: '#3B82F6', p: '#16A34A', d: '#D97706', f: '#9333EA', g: '#DC2626', h: '#0891B2', i: '#64748B',
};

export interface EnergyLevelsConfig {
  upTo?: number;
  highlightN?: number;
  title?: string;
  cue_steps?: unknown;
}

interface Props {
  config?: EnergyLevelsConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface Sublevel { letter: string; orbitals: number; electrons: number }
interface Level { n: number; sublevels: Sublevel[]; maxElectrons: number }

function buildLevels(upTo: number): Level[] {
  const out: Level[] = [];
  for (let n = 1; n <= upTo; n++) {
    const subs: Sublevel[] = [];
    for (let l = 0; l < n; l++) {
      const orbitals = 2 * l + 1;
      subs.push({ letter: SUBLEVEL_LETTERS[l] ?? '?', orbitals, electrons: orbitals * 2 });
    }
    out.push({ n, sublevels: subs, maxElectrons: 2 * n * n });
  }
  return out;
}

export default function EnergyLevels({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const upTo = Math.max(1, Math.min(7, config.upTo ?? 4));
  const levels = useMemo(() => buildLevels(upTo), [upTo]);

  // How many rows are revealed (self-demo walks them; cue can drive).
  const [revealed, setRevealed] = useState(reduced || hasCue ? upTo : 0);
  const [litN, setLitN] = useState<number | null>(config.highlightN ?? null);
  const [showTry, setShowTry] = useState(false);
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    if (reduced || hasCue) { setRevealed(upTo); return; }
    setRevealed(0);
    const timers: number[] = [];
    for (let i = 0; i < levels.length; i++) timers.push(window.setTimeout(() => setRevealed(i + 1), 450 * (i + 1)));
    timers.push(window.setTimeout(() => setShowTry(true), 450 * (levels.length + 1)));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upTo]);

  // Cue: progress reveals rows; step reveals up to a level; highlight an "n=k" / level token.
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);
    // Animate the row reveal only while PLAYING (step or progress); at rest
    // (paused) show the full table — never a one-row stub. computeCue can report a
    // step/progress even at the paused start, so both are gated on cue.play.
    if (cue.play === true && typeof cue.step === 'number') setRevealed(Math.max(0, Math.min(upTo, Math.round(cue.step))));
    else if (cue.play === true && typeof cue.progress === 'number') setRevealed(Math.round(Math.max(0, Math.min(1, cue.progress)) * upTo));
    else setRevealed(upTo);
    const toks = cueHighlights(cue);
    const hit = toks.map((t) => t.match(/(?:n=?)?(\d)/)).find(Boolean);
    if (hit) setLitN(Number(hit[1]));
  }, [cue, upTo]);

  const pick = (n: number) => {
    setLitN(n); setRevealed(upTo); setShowTry(false);
    if (!interacted) { setInteracted(true); onComplete?.({ type: 'energy_levels', n }); }
  };

  return (
    <VizCard
      eyebrow="Atomic structure · Energy levels"
      title={config.title ?? 'Energy levels, sublevels & electron capacity'}
      subtitle="Level n holds n sublevels and up to 2n² electrons"
      reducedMotion={reduced}
      hint="Each sublevel adds orbitals: s=1, p=3, d=5, f=7 (each orbital holds 2 e⁻)"
    >
      <div className="relative w-full overflow-x-auto">
        <NowYouTry show={showTry && !reduced && !hasCue} />
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-ws-ink-3">
              <th className="border-b border-ws-border px-2 py-1.5 text-center">Level&nbsp;n</th>
              <th className="border-b border-ws-border px-2 py-1.5 text-center">Sublevels</th>
              <th className="border-b border-ws-border px-2 py-1.5 text-left">Sublevels &amp; orbitals (2ℓ+1)</th>
              <th className="border-b border-ws-border px-2 py-1.5 text-center">Max e⁻ (2n²)</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((lv, i) => {
              const shown = i < revealed;
              const lit = litN === lv.n;
              return (
                <tr
                  key={lv.n}
                  onClick={() => pick(lv.n)}
                  className={`cursor-pointer transition-opacity ${shown ? 'opacity-100' : 'opacity-0'} ${lit ? 'bg-ws-accent-quiet' : 'hover:bg-ws-surface-2'}`}
                  style={{ transition: reduced ? undefined : 'opacity 300ms, background-color 150ms' }}
                >
                  <td className="border-b border-ws-border px-2 py-2 text-center font-mono text-lg font-bold text-ws-ink">{lv.n}</td>
                  <td className="border-b border-ws-border px-2 py-2 text-center font-semibold text-ws-ink-2">{lv.n}</td>
                  <td className="border-b border-ws-border px-2 py-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {lv.sublevels.map((s) => (
                        <span
                          key={s.letter}
                          className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-xs font-semibold text-white"
                          style={{ backgroundColor: SUBLEVEL_COLORS[s.letter] ?? '#64748B' }}
                          title={`${lv.n}${s.letter}: ${s.orbitals} orbital${s.orbitals > 1 ? 's' : ''}, ${s.electrons} e⁻`}
                        >
                          {lv.n}{s.letter}
                          <span className="opacity-80">·{s.orbitals}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="border-b border-ws-border px-2 py-2 text-center">
                    <span className={`font-mono text-base font-bold ${lit ? 'text-ws-accent' : 'text-ws-ink'}`}>{lv.maxElectrons}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-2 text-center text-[11px] text-ws-ink-3">
          Orbitals per sublevel: <b>s</b>=1, <b>p</b>=3, <b>d</b>=5, <b>f</b>=7 · each orbital holds <b>2</b> electrons · so level n holds <b>2n²</b>.
        </p>
      </div>
    </VizCard>
  );
}
